import { useState, useEffect, useCallback } from "react"
import { useTranslation } from "react-i18next"
import { useNavigate } from "react-router"
import {
  useListKnowledgeBaseNodesApi,
  useGetAgentsApi,
  agentResponseToAgent,
  knowledgeNodeResponseToKnowledgeBaseFolder,
} from "@/hooks/api"
import { useToast } from "@/hooks"
import { Workspace, Agent, AgentType } from "@/types"
import { useStore } from "@/store"
import { CustomizeAgentsView, AgentNewButton } from "./Agent"
import { getUserFacingApiErrorMessage } from "@/lib/apiErrors"

export interface AgentTabProps {
  workspace: Workspace
}

const AgentTab = ({ workspace }: AgentTabProps) => {
  const { t } = useTranslation()
  const { alertToast } = useToast()
  const navigate = useNavigate()
  const [userMe] = useStore((state) => [state.userMe])
  const organizationId = userMe?.profile?.defaultOrganizationId

  const [agents, setAgents] = useState<Agent[]>([])
  const [knowledgeBaseMap, setKnowledgeBaseMap] = useState<
    Map<string, { id: string; name: string }>
  >(new Map())

  // APIs
  const {
    data: knowledgeNodesResp,
    error: knowledgeNodesError,
    fetchFn: listKnowledgeNodes,
  } = useListKnowledgeBaseNodesApi()
  const {
    data: agentsResp,
    error: agentsError,
    fetchFn: getAgents,
  } = useGetAgentsApi()

  const refreshAgents = useCallback(() => {
    if (!organizationId) return
    getAgents({
      searchParams: new URLSearchParams({
        wid: workspace.id,
        oid: organizationId,
      }),
    })
  }, [getAgents, workspace.id, organizationId])

  useEffect(() => {
    if (!organizationId) return
    getAgents({
      searchParams: new URLSearchParams({
        wid: workspace.id,
        oid: organizationId,
      }),
    })
    listKnowledgeNodes({
      urlBuilderParams: {
        organizationId,
        workspaceId: workspace.id,
      },
    })
    // Intentionally exclude fetchFn deps (getAgents/listKnowledgeNodes):
    // these functions may not be referentially stable across renders and can cause repeated fetching.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [workspace.id, organizationId])

  useEffect(() => {
    if (!agentsResp) return
    setAgents(agentsResp.data?.map(agentResponseToAgent) || [])
  }, [agentsResp])

  useEffect(() => {
    if (!knowledgeNodesResp) return

    setKnowledgeBaseMap(
      new Map(
        (knowledgeNodesResp.data || [])
          // Only folders are selectable knowledge bases in unified tools.
          .filter((n) => n.node_type === "FOLDER")
          .map((n) => {
            const kb = knowledgeNodeResponseToKnowledgeBaseFolder(n)
            return [kb.id, kb]
          })
      )
    )
  }, [knowledgeNodesResp])

  // agents error handling
  useEffect(() => {
    if (!agentsError) return
    console.error("Failed to get agents", agentsError)

    alertToast({
      title: t("workspace.agent.message.getFailed.title"),
      description: getUserFacingApiErrorMessage(
        agentsError,
        t,
        agentsResp?.message || t("workspace.agent.message.getFailed.title")
      ),
    })
  }, [agentsError, agentsResp, alertToast, t])

  // knowledge bases error handling
  useEffect(() => {
    if (!knowledgeNodesError) return

    alertToast({
      title: "Failed to get knowledge bases",
      description: getUserFacingApiErrorMessage(
        knowledgeNodesError,
        t,
        knowledgeNodesResp?.message || "Failed to get knowledge bases"
      ),
    })
  }, [knowledgeNodesError, knowledgeNodesResp, alertToast, t])

  const handleAddBasicAgent = () => {
    navigate({
      pathname: `/workspaces/${workspace.id}/agents/new`,
      search: `?type=${AgentType.Basic}`,
    })
  }

  return (
    <>
      {agents.length > 0 && (
        <div className="mt-6 mb-4">
          <div className="text-sm my-4 dark:text-slate-400 text-slate-700">
            {t("workspace.agentTab.description")}
          </div>
          <div className="flex items-center justify-end">
            <AgentNewButton
              workspace={workspace}
              onNewBasic={handleAddBasicAgent}
            />
          </div>
        </div>
      )}
      <CustomizeAgentsView
        workspace={workspace}
        agents={agents}
        knowledgeBaseMap={knowledgeBaseMap}
        onRefreshAgents={refreshAgents}
        onNewAgent={handleAddBasicAgent}
      />
    </>
  )
}

export default AgentTab
